import { createSlice } from "@reduxjs/toolkit";

// SECURITY: Use localStorage for non-sensitive user data
// Tokens are NOT stored in localStorage/localStorage to prevent XSS attacks
// - Access tokens: Stored in memory (Redux state) only
// - Refresh tokens: Stored as httpOnly cookies by backend (not accessible to JS)

// Helper function to safely get from localStorage (non-sensitive data only)
const getFromStorage = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error);
    return null;
  }
};

// Helper function to safely set to localStorage (non-sensitive data only)
const setToStorage = (key, value) => {
  try {
    if (value !== null && value !== undefined) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.error(`Error writing ${key} to localStorage:`, error);
  }
};

// Initialize state from localStorage on app load
// SECURITY: Tokens are NOT restored from storage, only non-sensitive user data
// Access token will be restored via refresh token endpoint on app mount
const initialState = {
  userInfo: {
    username: getFromStorage("username"),
    role: getFromStorage("role"),
    userId: getFromStorage("userId"),
    token: null, // SECURITY: Never store tokens in storage
    refreshToken: null, // SECURITY: Refresh token is httpOnly cookie only
    email: getFromStorage("email"),
  },
  activeTab: getFromStorage("activeTab"),
  isAuthenticated: false, // Will be set to true after successful token refresh
  isLoading: false,
  isRestoringSession: true, // Tracks if session restoration is in progress
  isAIAgent: getFromStorage("isAIAgent") === "true",
};

const authUserSlice = createSlice({
  name: "authUser",
  initialState,
  reducers: {
    loginSuccess: (state, action) => {
      const { token, refresh, user_id, username, role, email } = action.payload;

      // Store tokens in memory only (Redux state)
      state.userInfo.token = token;
      state.userInfo.refreshToken = refresh; // Not actually used, refresh is httpOnly cookie
      state.userInfo.userId = user_id;
      state.userInfo.username = username;
      state.userInfo.role = role;
      state.userInfo.email = email;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.isRestoringSession = false; // Login completes restoration

      // SECURITY: Store only non-sensitive user data in localStorage
      // NEVER store tokens in localStorage/localStorage
      setToStorage("userId", user_id);
      setToStorage("username", username);
      setToStorage("role", role);
      if (email) setToStorage("email", email);
    },

    updateToken: (state, action) => {
      // SECURITY: Store token in memory only, never in storage
      state.userInfo.token = action.payload;
      state.isAuthenticated = true;
    },

    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
      setToStorage("activeTab", action.payload);
    },

    updateRole: (state, action) => {
      state.userInfo.role = action.payload;
      setToStorage("role", action.payload);
    },

    setIsAIAgent: (state, action) => {
      state.isAIAgent = action.payload;
      setToStorage("isAIAgent", action.payload ? "true" : "false");
    },

    logout: (state) => {
      state.userInfo.token = null;
      state.userInfo.refreshToken = null;
      state.userInfo.userId = null;
      state.userInfo.username = null;
      state.userInfo.role = null;
      state.userInfo.email = null;
      state.activeTab = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.isRestoringSession = false;
      state.isAIAgent = false;

      localStorage.clear();
    },

    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    restoreSession: (state) => {
      // SECURITY: Do NOT restore tokens from storage
      // Instead, restore only non-sensitive user data
      // The app will call refresh token endpoint to get a new access token
      state.userInfo.userId = getFromStorage("userId");
      state.userInfo.username = getFromStorage("username");
      state.userInfo.role = getFromStorage("role");
      state.userInfo.email = getFromStorage("email");
      state.activeTab = getFromStorage("activeTab");
      state.isAIAgent = getFromStorage("isAIAgent") === "true";
      // isAuthenticated will be set to true after successful token refresh
    },

    clearSession: (state) => {
      state.userInfo.token = null;
      state.userInfo.refreshToken = null;
      state.userInfo.userId = null;
      state.userInfo.username = null;
      state.userInfo.role = null;
      state.userInfo.email = null;
      state.activeTab = null;
      state.isAuthenticated = false;
      state.isAIAgent = false;
    },

    setRestoringSession: (state, action) => {
      state.isRestoringSession = action.payload;
    },

    sessionRestorationComplete: (state) => {
      state.isRestoringSession = false;
    },
  },
});

export const {
  loginSuccess,
  updateToken,
  setActiveTab,
  updateRole,
  setIsAIAgent,
  logout,
  setLoading,
  restoreSession,
  clearSession,
  setRestoringSession,
  sessionRestorationComplete,
} = authUserSlice.actions;

// Basic Selectors
export const selectAuthUser = (state) => state.authUser;
export const selectUserInfo = (state) => state.authUser.userInfo;
export const selectActiveTab = (state) => state.authUser.activeTab;
export const selectIsAuthenticated = (state) => state.authUser.isAuthenticated;
export const selectIsLoading = (state) => state.authUser.isLoading;
export const selectIsRestoringSession = (state) => state.authUser.isRestoringSession;
export const selectIsAIAgent = (state) => state.authUser.isAIAgent;

// Role-based permission selectors
export const selectIsAdmin = (state) => {
  const role = state.authUser.userInfo.role;
  return role === "Ensaras Admin" || role === "Client Admin";
};

export const selectIsEnsarasAdmin = (state) =>
  state.authUser.userInfo.role === "Ensaras Admin";
export const selectIsClientAdmin = (state) =>
  state.authUser.userInfo.role === "Client Admin";

// Feature permission selectors
export const selectCanExportData = (state) => selectIsAdmin(state);
export const selectCanViewAdminFeatures = (state) => selectIsAdmin(state);
export const selectCanManageUsers = (state) => selectIsAdmin(state);
export const selectCanManageTeams = (state) => selectIsAdmin(state);
export const selectCanViewAlerts = (state) => !!state.authUser.isAuthenticated;

// Complex permission selectors for specific features
export const selectCanAccessMqttFeatures = (state, teamId) => {
  // MQTT features available for specific teams
  const mqttEnabledTeams = [247, 37, 48];
  return (
    mqttEnabledTeams.includes(Number(teamId)) && state.authUser.isAuthenticated
  );
};

export default authUserSlice.reducer;
